/**
 * useInboundForeignKeys Hook
 *
 * Fetches inbound (referenced-by) foreign keys for the current table.
 * Uses Electron-owned metadata via Table Explorer IPC and TanStack Query caching.
 */

import { useQuery } from '@tanstack/react-query';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import { fetchTableExplorerInboundForeignKeys } from '../services/tableExplorerApi';

type UseInboundForeignKeysParams = {
  connectionId: string;
  database: string;
  schema: string;
  tableName: string;
  enabled: boolean;
};

export function useInboundForeignKeys(params: UseInboundForeignKeysParams) {
  const { connectionId, database, schema, tableName, enabled } = params;

  return useQuery<TableExplorerInboundForeignKey[]>({
    queryKey: ['table-inbound-foreign-keys', connectionId, database, schema, tableName],
    queryFn: async () => {
      if (!window.electronAPI?.tableExplorer?.fetchInboundForeignKeys) {
        console.warn('[useInboundForeignKeys] fetchInboundForeignKeys API not available');
        return [];
      }

      const result = await fetchTableExplorerInboundForeignKeys({
        connectionId,
        table: { database, schema, tableName },
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch inbound foreign keys');
      }

      return result.foreignKeys || [];
    },
    enabled: enabled && !!connectionId && !!tableName,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

